import React, { useEffect, useRef } from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { formatToMockDate, parseMockDateToIso } from "@/shared/utils/dateUtils";

export default function DatePickerButton({ selectedDate, onChange, allowClear = true, minDateIso, maxDateIso, allowedDatesIso }) {
  const inputRef = useRef(null);

  function handleClick() {
    if (inputRef.current) inputRef.current.showPicker ? inputRef.current.showPicker() : inputRef.current.click();
  }

  function onInputChange(e) {
    const val = e.target.value; // YYYY-MM-DD
    if (!val) {
      onChange?.(null);
      return;
    }
    // Allow any selected date; parent can validate if needed
    const d = new Date(val + "T00:00:00");
    onChange?.(formatToMockDate(d));
  }

  // Keep the hidden input in sync with the selected date and constraints
  useEffect(() => {
    if (!inputRef.current) return;
    const iso = selectedDate ? parseMockDateToIso(selectedDate) : "";
    inputRef.current.value = iso || "";
  }, [selectedDate]);

  function clear() {
    if (inputRef.current) inputRef.current.value = "";
    onChange?.(null);
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-secondary-200 rounded-md text-sm text-secondary-700 hover:bg-secondary-50"
        aria-label="Select date"
      >
        <CalendarIcon className="w-4 h-4 text-secondary-600" />
        <span className="text-sm text-secondary-700">{selectedDate ?? "Select date"}</span>
      </button>

      {allowClear && selectedDate ? (
        <button
          type="button"
          onClick={clear}
          className="inline-flex items-center justify-center w-8 h-8 bg-white border border-secondary-200 rounded-md text-secondary-500 hover:bg-secondary-50"
          aria-label="Clear date"
        >
          <X className="w-4 h-4" />
        </button>
      ) : null}

      <input
        ref={inputRef}
        type="date"
        onChange={onInputChange}
        min={minDateIso}
        max={maxDateIso}
        className="sr-only"
        aria-hidden="true"
      />
    </div>
  );
}
